/**
 * Blog & News article corpus for The Indian Pest Control / EntoRid.
 * All content is original, written in-house. Do NOT copy verbatim from
 * external sites. Update freely as the editorial calendar grows.
 *
 * Structure
 * ---------
 * Each article has:
 *   slug         – unique URL fragment
 *   type         – 'news' | 'guide'
 *   pest         – primary pest tag ('termite', 'cockroach', 'rodent',
 *                  'mosquito', 'general', etc.) — drives the horizontal tabs
 *   title, excerpt, author, publishedOn (ISO date), readMinutes
 *   heroAccent   – tailwind gradient class for the card / hero
 *   sections     – array of { heading, body[] | list[] | quote }
 *                  body[]  = paragraphs
 *                  list[]  = bullet items
 *                  faq[]   = { q, a } pairs
 *                  callout = highlighted note
 */

// ---------- Pest categories that drive the horizontal sub-menu ----------
export const BLOG_CATEGORIES = [
  { id: 'all',       label: 'All Articles' },
  { id: 'news',      label: 'Current News' },
  { id: 'termite',   label: 'Termites' },
  { id: 'cockroach', label: 'Cockroaches' },
  { id: 'rodent',    label: 'Rodents' },
  { id: 'mosquito',  label: 'Mosquitoes' },
  { id: 'bedbug',    label: 'Bed Bugs' },
  { id: 'ant',       label: 'Ants' },
  { id: 'fly',       label: 'Flies' },
  { id: 'spider',    label: 'Spiders' },
  { id: 'lizard',    label: 'Lizards' },
  { id: 'snake',     label: 'Snakes' },
  { id: 'silverfish',label: 'Silverfish' },
  { id: 'wasp',      label: 'Wasps & Bees' },
  { id: 'flea',      label: 'Fleas & Ticks' },
  { id: 'bird',      label: 'Birds' },
  { id: 'general',   label: 'Pest Control Guides' },
];

// =============================================================
// =====================  CURRENT NEWS  ========================
// =============================================================
// Five fresh, newspaper-styled stories. Original commentary —
// no facts copied verbatim from any publisher.
// =============================================================

const NEWS_ARTICLES = [
  {
    slug: 'news-termite-monsoon-surge-2026',
    type: 'news',
    pest: 'termite',
    title: 'Pre-Monsoon Heat Triggers Early Termite Swarms Across North Indian Homes',
    excerpt:
      'Service teams in Uttarakhand, Delhi-NCR and Western UP are reporting an unusually early termite swarm season. Here is what homeowners need to do this week.',
    author: 'EntoRid Editorial Desk',
    publishedOn: '2026-02-18',
    readMinutes: 4,
    heroAccent: 'from-amber-700 via-orange-600 to-red-600',
    dateline: 'DEHRADUN · UTTARAKHAND',
    sections: [
      {
        heading: 'A warmer-than-usual February is waking termite colonies early',
        body: [
          'Pest control field teams across Dehradun, Haridwar, Roorkee and parts of Western Uttar Pradesh have flagged a sharp uptick in winged-termite (alate) sightings during the second week of February — nearly four to five weeks ahead of the usual swarm cycle.',
          'Subterranean termite colonies typically wait for warm, humid evenings before releasing reproductives. Higher than normal day-time temperatures combined with damp soil from late-winter showers have created the exact conditions colonies need to launch a fresh expansion.',
        ],
      },
      {
        heading: 'What homeowners are noticing',
        list: [
          'Small piles of identical, translucent wings near windows, balcony doors and pooja-room corners.',
          'Fresh mud tubes climbing the outer plinth or rising from skirting boards.',
          'Doors and window frames that suddenly feel "spongy" or sound hollow when tapped.',
          'Tiny exit holes in plywood wardrobes that were silent through winter.',
        ],
      },
      {
        heading: 'Why an early swarm is bad news',
        body: [
          'Every swarming pair that finds soft, moist wood becomes a brand-new colony. A colony that begins in February will be fully active and breeding by May, which is when most homeowners notice damage to skirting, ply-wood and wooden furniture. By then the queen has already produced thousands of workers operating silently behind walls.',
        ],
      },
      {
        heading: 'Recommended action this week',
        list: [
          'Inspect plinth-level joints, bathroom walls and the back of wooden almirahs.',
          'Do not spray or disturb visible mud tubes — that only forces the colony to relocate inside the wall.',
          'Book a chemical perimeter treatment (anti-termite barrier) before the first heavy April shower; treatment windows fill quickly once swarming peaks.',
          'If you spot wings near a particular wall, photograph them with timestamp — it helps the technician trace the entry point.',
        ],
      },
      {
        callout:
          'Field tip from EntoRid technicians: 9 out of 10 termite "re-infestations" we attend in May are colonies that started silently in February–March. An early inspection is dramatically cheaper than a curative drilling treatment.',
      },
    ],
  },

  {
    slug: 'news-cockroach-resistance-2026',
    type: 'news',
    pest: 'cockroach',
    title: 'German Cockroaches in Urban Kitchens Are Shrugging Off Supermarket Sprays',
    excerpt:
      'A wave of complaints from apartment communities suggests over-the-counter cockroach sprays are losing punch — a familiar story of resistance, retold for 2026.',
    author: 'EntoRid Editorial Desk',
    publishedOn: '2026-02-12',
    readMinutes: 5,
    heroAccent: 'from-emerald-700 via-teal-700 to-slate-800',
    dateline: 'URBAN INDIA · APARTMENTS REPORT',
    sections: [
      {
        heading: 'Same can, same routine, more roaches',
        body: [
          'Across high-rise apartments in Dehradun, Lucknow, Noida and Pune, residents are noticing something frustrating — the cockroach spray that worked beautifully two summers ago barely makes a dent in 2026. Roaches scatter, recover within minutes, and reappear the same night.',
          'This is not magic. It is generational pesticide resistance — and German cockroaches (Blattella germanica) are world champions at developing it.',
        ],
      },
      {
        heading: 'Why supermarket sprays are losing the war',
        list: [
          'Most retail aerosols use the same two or three active ingredients, repeated for years.',
          'German cockroaches breed every 28–35 days, so resistant genes pass through populations in months, not decades.',
          'A spray that kills 90% of a colony still leaves 10% — and those are the ones with the genes to survive.',
          'Roaches also learn to avoid certain bait flavours, a behavioural resistance that DIY products cannot adjust for.',
        ],
      },
      {
        heading: 'How professional teams are responding',
        body: [
          'Licensed pest control technicians now rotate active molecules every quarter, use gel baits with multiple food matrices, and combine flushing agents with insect growth regulators (IGRs) that sterilise nymphs even when adults survive. The shift is away from "kill on contact" and toward "break the breeding cycle".',
        ],
      },
      {
        heading: 'For homeowners — what to change today',
        list: [
          'Stop spraying every roach you see; you are only training the colony.',
          'Wipe down kitchen sinks each night so roaches cannot drink — water is their bigger weakness, not food.',
          'Use sealed dustbins with foot pedals; open bins are gourmet buffets.',
          'Schedule a quarterly professional gel-baiting treatment instead of monthly DIY spraying.',
        ],
      },
      {
        callout:
          'A simple test: if you saw roaches yesterday and the same spray "worked", but you see them again within 72 hours — you are dealing with a breeding population, not stray visitors. Time to escalate.',
      },
    ],
  },

  {
    slug: 'news-rodent-warehouse-india-2026',
    type: 'news',
    pest: 'rodent',
    title: 'Indian Warehouses Sound the Alarm on Rising Rodent Activity Ahead of FSSAI Audits',
    excerpt:
      'F&B warehouses across North India are reporting a sharp rise in rodent gnawing damage — and FSSAI inspectors are tightening rejection thresholds in 2026.',
    author: 'EntoRid Editorial Desk',
    publishedOn: '2026-02-06',
    readMinutes: 4,
    heroAccent: 'from-slate-800 via-zinc-800 to-stone-900',
    dateline: 'WAREHOUSING · INDIA',
    sections: [
      {
        heading: 'A quiet problem with very loud consequences',
        body: [
          'Logistics managers across food and beverage warehouses in Haldwani, Rudrapur, Delhi-NCR and Western UP have flagged a steady rise in rodent-related damage this quarter — chewed-through cartons, contaminated grain, and gnawed insulation on cold-chain wiring.',
          'The numbers matter because audit thresholds under FSSAI’s 2026 hygiene framework have tightened. A single rodent-droppings finding in a finished-goods aisle is enough to trigger a re-audit and, in repeat cases, a temporary licence pause.',
        ],
      },
      {
        heading: 'What is driving the surge',
        list: [
          'Mild winter — rodent litters that would normally die off survived.',
          'Increase in cardboard-heavy packaging from e-commerce flows, which provides ideal nesting material.',
          'Gaps in periodic Integrated Pest Management (IPM) caused by staff transitions in many warehouses.',
          'Outdated reliance on poison baits without trapping, monitoring or proofing.',
        ],
      },
      {
        heading: 'Modern rodent-control programmes look very different',
        body: [
          'Professional teams now build a "rodent map" — a floor plan with numbered bait stations, snap traps and glue boards, each scanned during every visit with photo evidence. The data is shared with the warehouse manager every month, which means audit teams can see proactive controls, not just reactive cleaning.',
        ],
      },
      {
        callout:
          'For warehouse managers: ask your pest control partner for a written IPM (Integrated Pest Management) programme that includes proofing, monitoring, sanitation review and chemical control — not just baiting. That documentation is what auditors look at first.',
      },
    ],
  },

  {
    slug: 'news-mosquito-dengue-alert-2026',
    type: 'news',
    pest: 'mosquito',
    title: 'Health Departments Issue Early Dengue & Chikungunya Alert for the 2026 Pre-Monsoon Window',
    excerpt:
      'With an unusually warm February behind us, civic bodies are pushing residents and RWAs to begin mosquito source-reduction six to eight weeks earlier than usual.',
    author: 'EntoRid Editorial Desk',
    publishedOn: '2026-02-02',
    readMinutes: 4,
    heroAccent: 'from-rose-700 via-pink-600 to-red-700',
    dateline: 'PUBLIC HEALTH · NORTH INDIA',
    sections: [
      {
        heading: 'Why the alert came early this year',
        body: [
          'Municipal health departments across several North-Indian cities have begun issuing pre-monsoon mosquito advisories in February — a sharp shift from the usual April–May window. The trigger is a warmer-than-normal late winter combined with sporadic rainfall, which together extend the Aedes aegypti breeding season.',
          'Aedes aegypti is the primary vector for dengue and chikungunya. Unlike Anopheles (the malaria carrier), it bites by day and prefers clean, stagnant water inside our homes — a few millilitres in a flower-pot saucer is enough.',
        ],
      },
      {
        heading: 'What every household should do this fortnight',
        list: [
          'Walk through your balcony and roof once a week — empty every plant saucer, paint tin and unused bucket.',
          'Clean the overhead water tank and ensure the lid actually seals.',
          'Throw away tyres, broken pots and any other water-holding junk lying behind the house.',
          'Inspect AC drip-pan drains and bathroom floor traps — both are classic indoor breeding sites.',
        ],
      },
      {
        heading: 'For RWAs and apartment societies',
        body: [
          'Common-area mosquito control is more effective when it combines fogging (which only kills adults) with larviciding of stagnant pools and a documented inspection log of basements, parking lots and rooftop water tanks. A monthly mosquito audit is now considered baseline hygiene in well-managed societies.',
        ],
      },
      {
        callout:
          'Public-health reminder: only female mosquitoes bite, and only the ones that have already fed before lay eggs. Eliminating breeding water removes the next generation entirely — it is far more effective than chasing adults with sprays.',
      },
    ],
  },

  {
    slug: 'news-why-professional-pest-control',
    type: 'news',
    pest: 'general',
    title: 'Why Professional Pest Control Has Become Non-Negotiable for Modern Indian Homes',
    excerpt:
      'Higher-density apartments, denser pesticide resistance, more imported pests, stricter food safety — five forces are quietly turning pest control from a "nice-to-have" into routine home maintenance.',
    author: 'EntoRid Editorial Desk',
    publishedOn: '2026-01-28',
    readMinutes: 6,
    heroAccent: 'from-brand-blue via-blue-800 to-slate-900',
    dateline: 'OPINION · HOME SERVICES',
    sections: [
      {
        heading: 'A quiet shift in how Indian families think about their homes',
        body: [
          'A decade ago, "pest control" in most Indian homes meant a tin of repellent and a Sunday spray session. In 2026, ask any housing-society WhatsApp group and you will find pest control sitting next to fire safety, water-tank cleaning and lift maintenance — a scheduled, non-negotiable part of household upkeep.',
          'Five quiet trends explain the shift.',
        ],
      },
      {
        heading: '1. Denser living means denser pest populations',
        body: [
          'A single roach in a stand-alone house is a nuisance. The same roach in a 200-flat tower is a community problem — kitchen ducts and shared drains connect every flat. Modern construction designs were never optimised for pest exclusion, so professional treatment is the only way to break the shared network of nests.',
        ],
      },
      {
        heading: '2. DIY products are losing their punch',
        body: [
          'Pesticide resistance — covered in detail in our cockroach report — is now visible to ordinary homeowners. The aerosol that worked last summer barely irritates the roaches this year. Professionals can rotate active molecules and combine baits with insect growth regulators in ways supermarket cans simply cannot.',
        ],
      },
      {
        heading: '3. The cost of damage now dwarfs the cost of prevention',
        body: [
          'A modern apartment carries lakhs of rupees in plywood furniture, modular kitchens, electronic appliances and wiring. Termites or rodents can quietly chew through ten times the cost of a yearly pest control package before the problem is visible. Insurance does not cover pest damage.',
        ],
      },
      {
        heading: '4. Food safety expectations are rising',
        body: [
          'Whether you order in, host friends, or run a small business from home, food safety has gone from background concern to active expectation. A single roach photographed in a kitchen reel can ripple across social media for years. Professional, documented pest control is the simplest insurance against that risk.',
        ],
      },
      {
        heading: '5. Health risks are better understood',
        body: [
          'Pediatricians now routinely ask about pest exposure when investigating childhood asthma, eczema and recurrent infections. Cockroach allergens, rodent droppings and mosquito-borne viruses are no longer treated as folk worries — they are well-documented triggers, and that information has reached parents.',
        ],
      },
      {
        callout:
          'The honest bottom line: pest control is no longer about reacting to a swarm — it is about preventing one. Once a household runs the math on damage, health and time, scheduled professional service almost always wins on cost.',
      },
    ],
  },
];

// =============================================================
// =================  PEST-SPECIFIC GUIDES  ====================
// =============================================================
// One deep evergreen guide per priority pest.
// =============================================================

const PEST_GUIDES = [
  {
    slug: 'termite-complete-guide',
    type: 'guide',
    pest: 'termite',
    title: 'Termite Control in India: The Complete 2026 Guide for Homeowners',
    excerpt:
      'Everything an Indian homeowner needs to know about termites — how to spot them early, what treatments actually work, and how to keep them from coming back.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-20',
    readMinutes: 9,
    heroAccent: 'from-amber-600 via-orange-600 to-rose-700',
    sections: [
      {
        heading: 'What is a termite, really?',
        body: [
          'Termites are small social insects that feed on cellulose — the material that makes up wood, paper, plywood, cardboard, even certain natural fabrics. They look a little like pale ants, but they are biologically much closer to cockroaches. In a colony you will find a queen, a king, soldiers and a huge population of blind, soft workers who do all the damage.',
          'In India, the species most homes deal with is the subterranean termite — Heterotermes or Odontotermes. They live underground, walk up through hidden mud tubes, and chew wood from the inside out.',
        ],
      },
      {
        heading: 'Why termites are so dangerous',
        list: [
          'They feed silently — visible damage usually means months of hidden work.',
          'They can weaken structural beams, door frames and false ceilings.',
          'They destroy plywood wardrobes, kitchen cabinets and POP-finished walls.',
          'A mature colony can have over a million workers feeding 24×7.',
          'Home insurance in India almost never covers termite damage.',
        ],
      },
      {
        heading: 'How to spot termites early',
        list: [
          'Thin mud tubes on outer walls, plinth or skirting — the tube is a covered highway for workers.',
          'Doors and window frames that suddenly stick or feel "spongy".',
          'Tiny pin-holes in plywood with a fine sawdust-like powder underneath.',
          'Discarded translucent wings near windows after a humid evening — that is a swarm.',
          'A hollow sound when you tap a wooden surface.',
        ],
      },
      {
        heading: 'The two treatments that actually work',
        body: [
          'Anti-termite chemical treatments come in two main forms, and a good technician will choose between them based on your property:',
        ],
        list: [
          'Pre-construction treatment — a chemical barrier laid into the soil before the foundation is poured. The gold standard for a brand-new build.',
          'Post-construction treatment — drilling small holes at the plinth and slab levels and injecting a termiticide into the soil under and around the structure. This is what 95% of existing homes need.',
        ],
        callout:
          'Beware of "spraying" only. A surface spray on visible mud tubes feels satisfying but does nothing to the underground colony. A genuine treatment requires soil-level chemical injection.',
      },
      {
        heading: 'How long does a treatment last?',
        body: [
          'In Indian conditions, a quality post-construction termite treatment typically holds for 5 to 10 years, depending on soil type, drainage, the chemical used (typically Imidacloprid or Chlorpyrifos based), and how diligently the perimeter is maintained.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faq: [
          {
            q: 'Will the chemical smell linger in my home?',
            a: 'Modern termiticides used by professional teams are low-odour and quickly bind to the soil. Most homeowners do not notice any smell after 24 hours.',
          },
          {
            q: 'Do I need to vacate the house during the treatment?',
            a: 'Usually no. Treatments are done at plinth and soil level, not on living surfaces. Pets, children and elderly residents are typically asked to step out for 2–4 hours and that is it.',
          },
          {
            q: 'My wood is "termite-treated plywood". Am I safe?',
            a: 'That label refers to the ply you bought — not to your wall, your beams, or the soil beneath your home. Termites will simply walk past treated ply and feed on the next available cellulose.',
          },
          {
            q: 'How much does termite treatment cost in India?',
            a: 'For a 2BHK apartment, expect roughly ₹3,000–₹6,000 depending on the chemical and the warranty. For independent houses, pricing typically scales with carpet area and number of drill points.',
          },
          {
            q: 'What is the warranty on a treatment?',
            a: 'Reputable pest control companies offer 1 to 5 year written warranties on post-construction treatments. EntoRid offers up to 10 years on premium plans, with documented annual inspections.',
          },
        ],
      },
    ],
  },

  {
    slug: 'cockroach-elimination-guide',
    type: 'guide',
    pest: 'cockroach',
    title: 'Cockroach Elimination Guide: How to Get Rid of Roaches Permanently',
    excerpt:
      'A practical, no-fluff guide to identifying, treating and preventing cockroach infestations — the right way, without spraying yourself into a corner.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-15',
    readMinutes: 8,
    heroAccent: 'from-emerald-700 via-emerald-600 to-teal-700',
    sections: [
      {
        heading: 'Know your enemy first',
        body: [
          'Two cockroach species cause almost all the trouble in Indian homes. The German cockroach (small, light brown, sprinter) lives in kitchens, microwaves, drawers and motor housings. The American cockroach (large, reddish-brown, flies short distances) lives in drains, manholes and basements. They need different treatments.',
        ],
      },
      {
        heading: 'Why cockroaches are more than a nuisance',
        list: [
          'They contaminate exposed food with bacteria carried on their legs.',
          'Cockroach droppings and shed skins are a documented asthma trigger, especially in children.',
          'They produce a musty odour in heavy infestations — the smell itself is a giveaway.',
          'They breed shockingly fast; one female can produce 200–300 nymphs a year.',
        ],
      },
      {
        heading: 'Signs of an active infestation',
        list: [
          'Pepper-like droppings inside drawers and behind appliances.',
          'Egg cases (oothecae) — small, brown, capsule-shaped — stuck in corners.',
          'A musty, oily smell near the kitchen sink.',
          'Live roaches visible during the day (a sign the population has outgrown the hiding spaces).',
        ],
      },
      {
        heading: 'The treatment that actually breaks the colony',
        body: [
          'Forget the spray-and-pray approach. Modern cockroach control uses gel baiting — a tiny dab of slow-acting bait placed in dozens of strategic points. Workers feed on the gel, return to the harborage, and pass the toxin through the entire colony, including the egg-laying females. Done correctly, a single treatment can collapse a kitchen population within 7–14 days.',
        ],
      },
      {
        heading: 'For drains and manholes (American cockroach)',
        body: [
          'These roaches travel up plumbing stacks, so the treatment focuses on the drain network — residual spraying of basements, manhole interiors and floor traps, plus larvicides where standing water exists.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faq: [
          {
            q: 'How long does gel-bait treatment take to work?',
            a: 'You should see a major drop in activity within 5–7 days. Full population collapse typically takes 2–3 weeks. Do not spray during this period; you will scare them away from the bait.',
          },
          {
            q: 'Is it safe for kids and pets?',
            a: 'Yes. Modern gels are placed in inaccessible crevices and are formulated to be unattractive to mammals. EntoRid technicians log every gel placement and remove leftovers at the next visit.',
          },
          {
            q: 'How often should I re-treat?',
            a: 'For most homes, once every 3–6 months is enough. Restaurants and busy kitchens may need monthly maintenance.',
          },
          {
            q: 'Why do roaches keep coming back?',
            a: 'Almost always because of one of three reasons: an untreated neighbouring flat, an active drain pathway, or food sources left open at night. A professional inspection identifies which one applies to you.',
          },
        ],
      },
    ],
  },

  {
    slug: 'rodent-control-guide',
    type: 'guide',
    pest: 'rodent',
    title: 'Rodent Control: A Complete Guide to Eliminating Rats & Mice from Indian Homes',
    excerpt:
      'Rats are smart, suspicious and surprisingly clean. Here is how a structured rodent programme actually works — and why poison alone almost never solves the problem.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-10',
    readMinutes: 8,
    heroAccent: 'from-stone-700 via-zinc-700 to-slate-800',
    sections: [
      {
        heading: 'Why one rat sighting is never just one rat',
        body: [
          'Rats and mice are nocturnal, neophobic (suspicious of new objects) and social. If you have spotted one in daylight, the population behind it is almost certainly large enough that the nests are getting crowded. A single pair of rats can theoretically produce over 2,000 descendants in a year under ideal conditions.',
        ],
      },
      {
        heading: 'Signs you have a rodent problem',
        list: [
          'Droppings in straight lines along walls — rats travel along edges.',
          'Gnaw marks on cables, plastic pipes and even concrete corners.',
          'Greasy "rub marks" on walls and pipe joints where they squeeze through repeatedly.',
          'Scratching or scurrying sounds in the false ceiling at night.',
          'A characteristic ammonia-like smell in storerooms.',
        ],
      },
      {
        heading: 'Why poison alone is the wrong answer',
        body: [
          'Loose poison in a home creates three problems: rats can die inside walls and rot, pets and children are at risk, and surviving rats become bait-shy. Modern rodent control is built on a four-step structure:',
        ],
        list: [
          'Inspection and proofing — find every hole bigger than a 10-rupee coin and seal it.',
          'Monitoring — placing tamper-resistant bait stations and snap traps on a map.',
          'Sanitation review — finding the food source that is keeping rats interested.',
          'Documentation — every visit produces a report with what was found and what was treated.',
        ],
      },
      {
        heading: 'For commercial premises',
        body: [
          'Warehouses, restaurants and clinics need a written Integrated Pest Management (IPM) plan, numbered bait stations on a floor plan, and a monthly trend report. EntoRid provides this digitally as standard for all commercial AMCs.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faq: [
          {
            q: 'Will the rat die inside my walls?',
            a: 'When professionals use bait stations placed near entry points, rats almost always die in or near the station rather than deep inside cavities. The risk of in-wall mortality is far higher with random over-the-counter poisons.',
          },
          {
            q: 'How quickly will I see results?',
            a: 'Most homes see a 70–80% drop in activity within 10–14 days. A complete programme over 6–8 weeks closes the remaining population.',
          },
          {
            q: 'Do glue boards count as professional control?',
            a: 'They are a useful monitoring and trapping tool but cannot be the whole programme. Used alone, they only catch the bold rats and leave the cautious breeders behind.',
          },
          {
            q: 'I have a pet cat. Do I still need pest control?',
            a: 'Cats are excellent at suppressing visible activity but rarely solve a real infestation — rats simply route around them. A professional programme + a cat is a great combination.',
          },
        ],
      },
    ],
  },

  {
    slug: 'mosquito-control-guide',
    type: 'guide',
    pest: 'mosquito',
    title: 'Mosquito Control: Protecting Your Family from Dengue, Malaria & Chikungunya',
    excerpt:
      'A field-tested guide to mosquito control that goes far beyond plug-in repellents — what really works at the home, society and ward level.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-05',
    readMinutes: 7,
    heroAccent: 'from-rose-700 via-pink-600 to-fuchsia-700',
    sections: [
      {
        heading: 'Three mosquitoes, three different problems',
        list: [
          'Aedes aegypti — bites by day, breeds in clean water, carries dengue, chikungunya and Zika.',
          'Anopheles — bites at dawn and dusk, breeds in stagnant fresh water, carries malaria.',
          'Culex — the classic nuisance biter at night, breeds in dirty water and drains.',
        ],
        body: [
          'A good mosquito programme attacks the breeding water first and the adult population second — never the other way around.',
        ],
      },
      {
        heading: 'Step 1 — source reduction',
        list: [
          'Empty plant saucers, paint tins, broken pots and unused buckets weekly.',
          'Clean and seal overhead and underground water tanks.',
          'Clear roof drains and AC drip pans.',
          'Add a tight mesh over any open water storage.',
        ],
      },
      {
        heading: 'Step 2 — larviciding',
        body: [
          'For unavoidable stagnant water (society sumps, ornamental ponds, construction trenches), professional teams apply biological or chemical larvicides — products that kill the larvae before they ever become biting adults. This is far more cost-effective than fogging.',
        ],
      },
      {
        heading: 'Step 3 — adult control',
        body: [
          'Thermal fogging, ULV (ultra-low-volume) misting, and residual indoor spraying are the three workhorses for adult mosquitoes. They are most effective at dawn and dusk, when mosquitoes are active. Fogging alone, done without source reduction, is a temporary fix.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faq: [
          {
            q: 'Are the chemicals safe for children and asthma patients?',
            a: 'Yes — modern formulations like deltamethrin and lambda-cyhalothrin at registered concentrations are low toxicity to mammals. EntoRid teams advise a 1–2 hour ventilation period after indoor misting as a precaution.',
          },
          {
            q: 'How often should we fog the society?',
            a: 'Pre-monsoon (April–June): every 15 days. Monsoon (July–September): every 7–10 days. Post-monsoon: monthly. Combine with larviciding for sustainable control.',
          },
          {
            q: 'Do plug-in repellents and coils help?',
            a: 'They reduce biting in a single room but do nothing to the population. Treat them as comfort tools, not a control strategy.',
          },
          {
            q: 'I have a dengue patient at home. What should I do immediately?',
            a: 'Inform your local health centre, install mesh on doors and windows, sleep under a net, and request an emergency residual spray of the home and 200m radius from a licensed team.',
          },
        ],
      },
    ],
  },
];

// =============================================================
// =================  GENERAL / EVERGREEN  =====================
// =============================================================

const GENERAL_GUIDES = [
  {
    slug: 'what-is-pest-control',
    type: 'guide',
    pest: 'general',
    title: 'What Is Pest Control? A Plain-English Guide for Indian Homeowners',
    excerpt:
      'The basics of pest control — what it actually is, why it matters more than ever, and the seven everyday benefits that justify the cost.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-22',
    readMinutes: 5,
    heroAccent: 'from-brand-blue via-blue-700 to-indigo-700',
    sections: [
      {
        heading: 'A simple definition',
        body: [
          'Pest control is the structured process of identifying, managing, preventing and eliminating organisms that damage property, contaminate food, spread disease or otherwise interfere with comfortable living. It combines inspection, chemical treatment, mechanical exclusion and habit changes.',
        ],
      },
      {
        heading: 'Why pest control matters',
        list: [
          'Protects the health of every family member, especially children and the elderly.',
          'Prevents structural damage to walls, beams, doors and modular furniture.',
          'Eliminates disease vectors like mosquitoes, rats and cockroaches.',
          'Improves overall hygiene and indoor air quality.',
          'Safeguards food supplies from contamination.',
          'Maintains — and even raises — the resale value of your property.',
          'Prevents the much larger cost of curative repairs later.',
        ],
      },
      {
        heading: 'Reactive vs preventive',
        body: [
          'Most Indian homes still treat pest control as a reaction to a problem. The shift toward preventive, scheduled service is the single biggest cost-saving change a family can make. A yearly preventive contract typically costs less than one curative termite repair.',
        ],
      },
    ],
  },

  {
    slug: 'what-are-pests-and-why-they-enter-homes',
    type: 'guide',
    pest: 'general',
    title: 'What Are Pests, and Why Do They Enter Indian Homes?',
    excerpt:
      'A field guide to the 13 most common household pests in India and the three universal reasons they choose your home over the next one.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-18',
    readMinutes: 6,
    heroAccent: 'from-teal-700 via-emerald-700 to-green-700',
    sections: [
      {
        heading: 'Common household pests in India',
        list: [
          'Termites',
          'Cockroaches',
          'Bed bugs',
          'Rats and mice',
          'Mosquitoes',
          'Ants (especially sugar and ghost ants)',
          'Houseflies and fruit flies',
          'Spiders',
          'Silverfish',
          'Wasps and bees',
          'Fleas and ticks (on pets)',
          'Lizards and geckos',
          'Birds (pigeons) in ledges and AC units',
        ],
      },
      {
        heading: 'Why pests choose your home',
        body: [
          'Every infestation, no matter the species, boils down to three resources the pest is hunting for.',
        ],
        list: [
          'Food — open containers, exposed leftovers, pet bowls, kitchen spills, even tea-stained mugs.',
          'Water — dripping taps, AC drains, damp walls, plant saucers, leaky pipes.',
          'Shelter — cracks in walls, false ceilings, the back of wooden furniture, storage clutter, unused store rooms.',
        ],
        callout:
          'Remove any one of these three reliably and the population shrinks. Remove all three and most species cannot establish a colony in the first place.',
      },
    ],
  },

  {
    slug: 'pros-of-professional-pest-control-and-cons-of-ignoring',
    type: 'guide',
    pest: 'general',
    title: 'Pros of Professional Pest Control & The Hidden Cost of Ignoring It',
    excerpt:
      'A clear comparison of what families gain when they switch to scheduled service — and what they silently lose by waiting for the next infestation.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-12',
    readMinutes: 6,
    heroAccent: 'from-indigo-700 via-violet-700 to-purple-700',
    sections: [
      {
        heading: 'Pros of professional pest control',
        list: [
          'Long-term protection — treatments last months or years, not days.',
          'Health protection — fewer asthma triggers, food contaminants and vector-borne illnesses.',
          'Property protection — early action against termites and rodents avoids structural damage.',
          'Cost savings — a single termite repair often costs more than 5–10 years of preventive contracts.',
          'Peace of mind — a clean, documented home is a calmer home.',
        ],
      },
      {
        heading: 'Cons of ignoring pest control',
        list: [
          'Allergies and asthma triggered by cockroach droppings and dust mites.',
          'Hidden termite damage to plywood, door frames and beams.',
          'Rodent-chewed wiring — a leading cause of "unexplained" electrical fires.',
          'Reputation damage for home-run businesses, tiffin services and cloud kitchens.',
          'Higher mosquito-borne disease risk during monsoon.',
        ],
      },
    ],
  },

  {
    slug: 'pest-control-faq-everything-you-asked',
    type: 'guide',
    pest: 'general',
    title: 'Pest Control FAQ: 40+ Questions Indian Customers Actually Ask',
    excerpt:
      'A single, scannable answer file covering safety, timing, cost, follow-ups, DIY, kids, pets, allergies and more.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-08',
    readMinutes: 12,
    heroAccent: 'from-cyan-700 via-sky-700 to-blue-800',
    sections: [
      {
        heading: 'Safety',
        faq: [
          { q: 'Is pest control safe for children?',
            a: 'Modern professional treatments are formulated to be safe for children when applied according to label guidelines. Technicians will advise specific ventilation and re-entry times.' },
          { q: 'Is pest control safe for pets?',
            a: 'Yes. Pet owners are usually asked to relocate cages, bowls and bedding to one room during the treatment, and to re-enter the treated area after the recommended ventilation period.' },
          { q: 'Will the smell linger?',
            a: 'Most modern formulations are low-odour. Any residual smell typically dissipates within 1–2 hours of ventilation.' },
          { q: 'Are pest control chemicals harmful to plants?',
            a: 'Indoor plants are generally fine if the technician avoids direct spraying on leaves. For outdoor gardens, ask for plant-safe formulations.' },
        ],
      },
      {
        heading: 'Timing',
        faq: [
          { q: 'How long does a general pest treatment take?',
            a: 'Most residential treatments take 30–90 minutes. Termite treatments can run several hours because of drilling and injection.' },
          { q: 'Do I need to leave my home during the service?',
            a: 'It depends on the treatment type and family members. Most general treatments allow you to stay. Termite and bed-bug treatments may require a 2–4 hour absence.' },
          { q: 'Can I stay in the room immediately after treatment?',
            a: 'Follow the technician’s ventilation guidance. As a rule, ventilate for 30 minutes before re-entering and 1–2 hours before sleeping in the room.' },
          { q: 'How often should pest control be done?',
            a: 'Residential homes: every 3–6 months. Commercial kitchens and warehouses: monthly or quarterly. High-risk areas: monthly inspection.' },
        ],
      },
      {
        heading: 'Cost & Value',
        faq: [
          { q: 'How much does pest control cost in India?',
            a: 'Costs vary with property size, pest type and severity. A 2BHK general pest treatment typically falls between ₹999–₹2,500. Termite treatments range higher because of drilling and chemicals used.' },
          { q: 'Is pest control worth the cost?',
            a: 'Almost always yes — the cost of preventing damage and disease is a small fraction of the cost of curative repairs and hospital visits.' },
          { q: 'What does a warranty actually cover?',
            a: 'A pest control warranty typically guarantees free re-treatment if the same pest reappears within the warranty period. EntoRid offers transparent, written warranty terms on every booking.' },
        ],
      },
      {
        heading: 'Treatment effectiveness',
        faq: [
          { q: 'Does pest control kill eggs too?',
            a: 'Some treatments target eggs (such as IGRs for cockroaches and bed bugs). Many treatments require a follow-up visit to address the next generation of nymphs.' },
          { q: 'Why do pests come back after treatment?',
            a: 'Common reasons: untreated entry points, neighbouring infestations, food and water sources still available, or skipped follow-up visits.' },
          { q: 'Is DIY pest control effective?',
            a: 'DIY can provide temporary relief for visible pests but rarely eliminates a hidden colony. It also trains the population to be more resistant over time.' },
          { q: 'Can I combine pest control with home cleaning?',
            a: 'Yes — but do the cleaning first and the pest control after. Wiping kitchen surfaces after gel-bait placement will undo the treatment.' },
        ],
      },
    ],
  },

  {
    slug: 'pest-prevention-tips-every-home',
    type: 'guide',
    pest: 'general',
    title: 'Pest Prevention Tips for Every Indian Home — Room by Room',
    excerpt:
      'A room-by-room checklist that quietly cuts your need for pest control in half. No chemicals, no tools, just routine habits.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2026-01-04',
    readMinutes: 5,
    heroAccent: 'from-lime-600 via-emerald-600 to-teal-700',
    sections: [
      {
        heading: 'Kitchen',
        list: [
          'Store dry food in airtight containers — not the original paper packets.',
          'Wipe spills the moment they happen; sugar and oil residue is the #1 attractant.',
          'Empty the dustbin daily, and use a foot-pedal bin with a tight lid.',
          'Run the sink trap with hot water once a week to keep drain roaches away.',
        ],
      },
      {
        heading: 'Bathroom',
        list: [
          'Fix dripping taps and leaking pipes within days — every drip is a free water supply.',
          'Ventilate after showers — damp walls invite silverfish and cockroaches.',
          'Clean the floor trap monthly.',
        ],
      },
      {
        heading: 'Bedroom',
        list: [
          'Vacuum mattresses and the area under the bed every fortnight.',
          'Avoid storing cardboard boxes under the bed (perfect bed-bug harbourage).',
          'Wash bed linen at 60°C if anyone has been travelling on long-distance trains or staying in budget hotels.',
        ],
      },
      {
        heading: 'Living room & balcony',
        list: [
          'Empty plant saucers weekly to stop mosquito breeding.',
          'Trim plants away from walls — they bridge ants and lizards into the house.',
          'Seal cable entry points with silicone.',
        ],
      },
      {
        heading: 'Outdoors',
        list: [
          'Remove standing water in tyres, broken pots and unused buckets.',
          'Keep firewood and brick piles at least 30cm away from outer walls.',
          'Seal cracks at the plinth level — that is the termite highway.',
        ],
      },
    ],
  },

  {
    slug: 'pest-control-myths-vs-facts',
    type: 'guide',
    pest: 'general',
    title: 'Pest Control Myths vs Facts: What Indian Families Get Wrong',
    excerpt:
      'Ten myths that delay real pest control — and the simple facts that replace them.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2025-12-30',
    readMinutes: 5,
    heroAccent: 'from-yellow-600 via-orange-600 to-red-600',
    sections: [
      {
        heading: 'Myth 1',
        body: [
          'Myth: "If I don’t see pests, I don’t have pests."',
          'Fact: Most pests are nocturnal or live inside walls. By the time you see them in daylight, the population has outgrown its hiding spaces.',
        ],
      },
      {
        heading: 'Myth 2',
        body: [
          'Myth: "Termites only attack old homes."',
          'Fact: Termites attack any cellulose material. New buildings without a pre-construction barrier are just as vulnerable.',
        ],
      },
      {
        heading: 'Myth 3',
        body: [
          'Myth: "One pest just means one pest."',
          'Fact: A visible pest usually means a breeding population is hidden somewhere nearby.',
        ],
      },
      {
        heading: 'Myth 4',
        body: [
          'Myth: "Pest control chemicals are too harsh for kids and pets."',
          'Fact: Modern formulations used by licensed professionals are far safer than the random supermarket sprays families typically use as a substitute.',
        ],
      },
      {
        heading: 'Myth 5',
        body: [
          'Myth: "A clean home cannot have pests."',
          'Fact: Cleanliness reduces, but does not eliminate, the risk. Pests also enter through plumbing, balconies and shared walls.',
        ],
      },
      {
        heading: 'Myth 6',
        body: [
          'Myth: "Once treated, I am safe forever."',
          'Fact: All treatments have a lifespan. Annual or quarterly maintenance keeps the protection alive.',
        ],
      },
      {
        heading: 'Myth 7',
        body: [
          'Myth: "Cats and dogs will keep rats away."',
          'Fact: Pets help, but professional bait stations and proofing close the gaps a cat cannot reach.',
        ],
      },
      {
        heading: 'Myth 8',
        body: [
          'Myth: "Pest control kills only adult pests."',
          'Fact: Modern treatments combine adulticides with insect growth regulators (IGRs) to break the breeding cycle.',
        ],
      },
      {
        heading: 'Myth 9',
        body: [
          'Myth: "DIY sprays are just as effective."',
          'Fact: DIY products use the same actives for years, creating resistant populations. Licensed teams rotate molecules every season.',
        ],
      },
      {
        heading: 'Myth 10',
        body: [
          'Myth: "Pest control is too expensive."',
          'Fact: Termite damage to a single modular kitchen often costs more than 10 years of preventive contracts.',
        ],
      },
    ],
  },

  {
    slug: 'emergency-signs-you-need-pest-control',
    type: 'guide',
    pest: 'general',
    title: 'Emergency Signs You Need Pest Control Immediately',
    excerpt:
      'Seven warning signs that tell you the inspection cannot wait until next month.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2025-12-22',
    readMinutes: 4,
    heroAccent: 'from-red-700 via-rose-700 to-orange-700',
    sections: [
      {
        heading: 'Treat these as a call-the-team-today list',
        list: [
          'Visible mud tubes on walls, plinth or skirting boards.',
          'Rodents seen during daytime — the population has outgrown its hiding spots.',
          'Large numbers of cockroaches in the kitchen even after a clean-up.',
          'Multiple bed-bug bites on a family member or guest after a single night.',
          'Mosquito breeding pools spotted in your balcony or terrace.',
          'Wood that suddenly sounds hollow or feels spongy when you press it.',
          'Chewed electrical wires or insulation in the panel cupboard.',
        ],
      },
    ],
  },

  {
    slug: 'residential-pest-control-guide',
    type: 'guide',
    pest: 'general',
    title: 'Residential Pest Control Guide: Apartments, Villas & Farmhouses',
    excerpt:
      'Different homes attract different pests. Here is what to expect — and what to schedule — for each living style.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2025-12-18',
    readMinutes: 6,
    heroAccent: 'from-blue-700 via-cyan-700 to-emerald-700',
    sections: [
      {
        heading: 'Apartments',
        body: [
          'Apartments share plumbing stacks, ducting, false ceilings and even structural cavities with dozens of other units. The pests follow.',
        ],
        list: [
          'Common issues: German cockroaches, ants, bed bugs and the occasional rodent in basements and ducts.',
          'Recommended cycle: quarterly general pest treatment + annual cockroach gel-baiting.',
          'Look out for: society-level mosquito control, shared waste rooms and basement parking.',
        ],
      },
      {
        heading: 'Independent villas',
        body: [
          'Villas have lawns, plinths, attics and storage rooms that apartments do not — and that is where the action is.',
        ],
        list: [
          'Common issues: termites, rodents in store rooms, mosquitoes in gardens.',
          'Recommended cycle: annual termite inspection, monthly mosquito fogging in summer, quarterly rodent monitoring.',
          'Look out for: rainwater drainage that creates damp pockets near outer walls.',
        ],
      },
      {
        heading: 'Farmhouses & weekend homes',
        body: [
          'Farmhouses sit in or near agricultural fields, with vegetation right up to the wall — perfect for snakes, rodents and termites.',
        ],
        list: [
          'Common issues: rodents, termites, snakes, mosquitoes, occasionally bats and birds.',
          'Recommended cycle: every visit + a pre-stay opening protocol.',
          'Look out for: untouched water tanks and unused rooms between stays.',
        ],
      },
    ],
  },

  {
    slug: 'commercial-pest-control-guide',
    type: 'guide',
    pest: 'general',
    title: 'Commercial Pest Control Guide: Restaurants, Hotels, Warehouses & Offices',
    excerpt:
      'How professional Integrated Pest Management (IPM) actually looks inside the businesses that cannot afford a single pest sighting.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2025-12-14',
    readMinutes: 7,
    heroAccent: 'from-slate-800 via-zinc-800 to-blue-900',
    sections: [
      {
        heading: 'Restaurants & cloud kitchens',
        list: [
          'Primary risks: German cockroaches, rodents, flies.',
          'Recommended cycle: weekly inspection + monthly gel baiting + drain treatment.',
          'Compliance: maintain documented pest control logs for FSSAI audits.',
        ],
      },
      {
        heading: 'Hotels & service apartments',
        list: [
          'Primary risks: bed bugs, cockroaches, ants in pantries, mosquitoes in landscaping.',
          'Recommended cycle: room-by-room bed-bug monitoring + monthly general treatment.',
          'Compliance: zero-tolerance approach to reviews mentioning insects.',
        ],
      },
      {
        heading: 'Warehouses & cold chains',
        list: [
          'Primary risks: rodents, stored-product insects, birds at loading bays.',
          'Recommended cycle: a written IPM plan with numbered bait stations, monthly audit and trend reports.',
          'Compliance: AIB, FSSAI and ISO22000 frameworks expect documented preventive controls.',
        ],
      },
      {
        heading: 'Offices, clinics & schools',
        list: [
          'Primary risks: ants, occasional roaches in pantries, mosquitoes near washrooms.',
          'Recommended cycle: quarterly preventive treatment + emergency callouts.',
          'Compliance: low-odour, food-safe formulations during working hours.',
        ],
      },
    ],
  },

  {
    slug: 'diy-vs-professional-pest-control',
    type: 'guide',
    pest: 'general',
    title: 'DIY vs Professional Pest Control: An Honest Comparison',
    excerpt:
      'When does DIY actually work, and when does it quietly make things worse? A balanced look from teams who handle both ends of the spectrum.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2025-12-08',
    readMinutes: 5,
    heroAccent: 'from-orange-700 via-amber-700 to-yellow-700',
    sections: [
      {
        heading: 'Where DIY does work',
        list: [
          'Killing one or two visible mosquitoes inside a single room.',
          'A short-term knockdown of a roach you spotted on the counter.',
          'Routine cleaning and sealing of food containers.',
        ],
      },
      {
        heading: 'Where DIY quietly fails',
        list: [
          'Termites — by the time you see them, the colony is already underground.',
          'Bed bugs — they hide in seams DIY sprays cannot reach.',
          'Rodents — bait shyness develops in 2–3 weeks of supermarket poisons.',
          'Cockroach colonies — surface sprays scatter them deeper into wall cavities.',
        ],
      },
      {
        heading: 'A simple rule of thumb',
        body: [
          'If you saw the pest more than three times in a week, you are looking at a population, not a visitor. That is when professional treatment beats DIY on every dimension — speed, cost, safety and long-term effectiveness.',
        ],
      },
    ],
  },

  {
    slug: 'safe-for-children-and-pets',
    type: 'guide',
    pest: 'general',
    title: 'Is Pest Control Safe for Children & Pets? A No-Spin Guide',
    excerpt:
      'A straight answer to the question every parent and pet owner asks before saying yes to a treatment.',
    author: 'EntoRid Knowledge Team',
    publishedOn: '2025-12-02',
    readMinutes: 4,
    heroAccent: 'from-pink-600 via-rose-600 to-red-600',
    sections: [
      {
        heading: 'The short answer',
        body: [
          'Yes — when done by a licensed team using registered formulations and proper application protocols. The bigger risk in most homes is the random store-bought spray used without protective gear, ventilation guidance or dosage control.',
        ],
      },
      {
        heading: 'Sensible precautions on the day of treatment',
        list: [
          'Move pet bowls, cages and bedding to one untreated room.',
          'Cover or remove aquariums (fish are very sensitive to certain formulations).',
          'Ask children to stay out of treated rooms during application.',
          'Ventilate the treated rooms for 30–60 minutes before re-entry.',
        ],
      },
      {
        heading: 'When to be extra cautious',
        list: [
          'A family member with respiratory illness — request low-odour, water-based formulations.',
          'Pregnancy — let the technician know in advance; certain treatments will be re-scheduled.',
          'Infants under 1 year — ventilate for longer and avoid putting them on the treated floor for 24 hours.',
        ],
      },
    ],
  },
];

// =============================================================
// ===================  EXPORTS & HELPERS  =====================
// =============================================================

export const BLOG_ARTICLES = [
  ...NEWS_ARTICLES,
  ...PEST_GUIDES,
  ...GENERAL_GUIDES,
];

/** Return article count per category id (drives the "Termite 1", "Lizard 0" badges). */
export function getCategoryCounts() {
  const counts = {};
  for (const cat of BLOG_CATEGORIES) {
    if (cat.id === 'all') counts[cat.id] = BLOG_ARTICLES.length;
    else if (cat.id === 'news') counts[cat.id] = BLOG_ARTICLES.filter(a => a.type === 'news').length;
    else counts[cat.id] = BLOG_ARTICLES.filter(a => a.pest === cat.id).length;
  }
  return counts;
}

/** Return the article list for a given category id. */
export function getArticlesByCategory(catId) {
  if (catId === 'all') return BLOG_ARTICLES;
  if (catId === 'news') return BLOG_ARTICLES.filter(a => a.type === 'news');
  return BLOG_ARTICLES.filter(a => a.pest === catId);
}

/** Look up a single article by slug. */
export function getArticleBySlug(slug) {
  return BLOG_ARTICLES.find(a => a.slug === slug) || null;
}

/** Return up to N articles related to the given one (same pest, different slug). */
export function getRelatedArticles(article, limit = 3) {
  if (!article) return [];
  return BLOG_ARTICLES
    .filter(a => a.slug !== article.slug && (a.pest === article.pest || a.type === article.type))
    .slice(0, limit);
}
